import { useState } from "react"
import { Check, Copy, CircleHelp } from "lucide-react"
import { useTranslation } from "react-i18next"
import { Avatar, AvatarFallback } from "@strongtie/design-system/avatar"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@strongtie/design-system/dropdown-menu"
import { useAuth } from "@/hooks/use-auth"
import { useVersionInfo } from "@/hooks/use-version-info"
import { useCurrentTenant } from "@/hooks/use-current-tenant"
import { HELP_URL } from "@/lib/utils"

export function ProfileMenu() {
  const { t } = useTranslation()
  const { user, logout } = useAuth()
  const { data: versionInfo } = useVersionInfo()
  const { data: tenant } = useCurrentTenant()
  const [copied, setCopied] = useState(false)

  const email = user?.profile?.email
  const initial = email ? email.charAt(0).toUpperCase() : "?"

  const versionText =
    `CS Reporting Manager: ${versionInfo?.reportingManagerVersion ?? "—"}\n` +
    `Web Service: ${versionInfo?.reportingWebServiceVersion ?? "—"}\n` +
    `Reporting Assembly: ${versionInfo?.reportingAssemblyVersion ?? "—"}`

  const handleCopyVersion = async () => {
    await navigator.clipboard.writeText(versionText)
    setCopied(true)
    setTimeout(() => setCopied(false), 2000)
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          aria-label="Profile menu"
          className="focus-visible:ring-ring rounded-full focus-visible:ring-2 focus-visible:outline-none"
        >
          <Avatar>
            <AvatarFallback className="bg-orange-500 font-medium text-white">{initial}</AvatarFallback>
          </Avatar>
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-72">
        {email && <DropdownMenuLabel>{email}</DropdownMenuLabel>}
        <DropdownMenuSeparator />
        <DropdownMenuItem asChild>
          <a href={HELP_URL} target="_blank" rel="noreferrer">
            <CircleHelp className="h-4 w-4" />
            {t("profileMenu.help")}
          </a>
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        <DropdownMenuItem
          aria-label={t("profileMenu.copyVersion")}
          onSelect={(event) => {
            event.preventDefault()
            void handleCopyVersion()
          }}
        >
          <div className="flex w-full items-center justify-between">
            <span className="text-muted-foreground text-xs font-medium">{t("profileMenu.version")}</span>
            <span aria-hidden="true">
              {copied ? <Check className="h-3.5 w-3.5" /> : <Copy className="h-3.5 w-3.5" />}
            </span>
          </div>
        </DropdownMenuItem>
        <div className="space-y-1 px-1.5 pb-1 text-sm">
          <div>
            <span className="font-medium">CS Reporting Manager: </span>
            <span>{versionInfo?.reportingManagerVersion ?? "—"}</span>
          </div>
          <div>
            <span className="font-medium">Web Service: </span>
            <span>{versionInfo?.reportingWebServiceVersion ?? "—"}</span>
          </div>
          <div>
            <span className="font-medium">Reporting Assembly: </span>
            <span>{versionInfo?.reportingAssemblyVersion ?? "—"}</span>
          </div>
        </div>
        <DropdownMenuSeparator />
        <DropdownMenuLabel>
          {t("profileMenu.tenant")}: {tenant?.name ?? "—"}
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        <DropdownMenuItem onClick={() => logout()}>{t("profileMenu.logOut")}</DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  )
}
